<template>
  <div>
    <div
      class="rt-alarm-overlay"
      :class="{ open: dialogVisible }"
      @click.self="handleClose"
    >
      <div class="rt-alarm-modal" role="dialog" aria-modal="true">
        <div class="rt-alarm-head">
          <h3>告警提示</h3>
          <span class="num" v-if="alertList.length"
            >（{{ alertList.length }} 条待确认）</span
          >
          <button class="close" type="button" title="关闭" @click="handleClose">
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div class="rt-alarm-body">
          <div
            v-for="item in alertList"
            :key="item.id"
            class="rt-alarm-item"
            :class="{ removing: item.removing }"
          >
            <p v-html="formatAlertText(item.text)"></p>
            <button class="ok" type="button" @click="handleAck(item)">
              确认
            </button>
          </div>
          <div v-if="!alertList.length" class="rt-alarm-empty">
            当前暂无待确认告警
          </div>
        </div>
      </div>
    </div>
    <div class="rt-alarm-toast" :class="{ show: toastVisible }">
      {{ toastText }}
    </div>
  </div>
</template>

<script>
import { apiData } from "../api/index";

export default {
  props: {
    showRealTimeAlarmDialog: {
      type: Boolean,
      default: false,
    },
    realTimeAlarm: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      alertList: [],
      toastVisible: false,
      toastText: "",
      toastTimer: null,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.showRealTimeAlarmDialog;
      },
      set(val) {
        this.$emit("update:showRealTimeAlarmDialog", val);
      },
    },
  },
  watch: {
    showRealTimeAlarmDialog: {
      handler(visible) {
        this.$nextTick(() => {
          if (visible) {
            this.playAlarm();
          } else {
            this.stopAlarm();
          }
        });
      },
      immediate: true,
    },
    realTimeAlarm: {
      handler(val) {
        this.alertList = this.normalizeMsgs(val && val.msgs);
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    document.addEventListener("keydown", this.handleEsc);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleEsc);
    this.stopAlarm();
    clearTimeout(this.toastTimer);
  },
  methods: {
    normalizeMsgs(msgs) {
      if (!Array.isArray(msgs)) {
        return [];
      }
      return msgs
        .map((item, index) => {
          if (typeof item === "string") {
            return { id: String(index), text: item, removing: false };
          }
          return {
            id: item.id,
            text: item.cont || item.text || item.msg || item.content || "",
            title: item.title,
            removing: false,
          };
        })
        .filter((item) => item.text);
    },
    formatAlertText(text) {
      if (!text) {
        return "";
      }
      const escaped = String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      return escaped.replace(/^(【[^】]+】)/, "<b>$1</b>");
    },
    handleEsc(e) {
      if (e.key === "Escape" && this.dialogVisible) {
        this.handleClose();
      }
    },
    playAlarm() {
      const audio = this.$refs.alarmAudio;
      if (!audio) {
        return;
      }
      audio.loop = true;
      audio.currentTime = 0;
      const playPromise = audio.play();
      if (playPromise && playPromise.catch) {
        playPromise.catch(() => {});
      }
    },
    stopAlarm() {
      const audio = this.$refs.alarmAudio;
      if (!audio) {
        return;
      }
      audio.pause();
      audio.currentTime = 0;
    },
    handleClose() {
      this.dialogVisible = false;
    },
    showToast(msg) {
      this.toastText = msg;
      this.toastVisible = true;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toastVisible = false;
      }, 2600);
    },
    removeItem(id) {
      const targetId = String(id);
      const target = this.alertList.find(
        (item) => String(item.id) === targetId
      );
      if (target) {
        this.$set(target, "removing", true);
      }
      setTimeout(() => {
        this.alertList = this.alertList.filter(
          (item) => String(item.id) !== targetId
        );
        const remainIds = this.alertList.map((item) => String(item.id));
        const sourceMsgs =
          (this.realTimeAlarm && this.realTimeAlarm.msgs) || [];
        const nextMsgs = sourceMsgs.filter((item) =>
          remainIds.includes(String(item && item.id))
        );
        this.$emit("update:realTimeAlarm", {
          ...(this.realTimeAlarm || {}),
          msgs: nextMsgs,
          popup: nextMsgs.length > 0,
        });
        if (!this.alertList.length) {
          this.dialogVisible = false;
          this.showToast("全部告警已确认");
        }
      }, 320);
    },
    handleAck(item) {
      if (!item || item.removing) {
        return;
      }
      const id = item.id;
      if (!id && id !== 0) {
        this.removeItem(id);
        return;
      }
      apiData
        .queryRealTimeAlarmAck(id)
        .then((res) => {
          if (!res || res.status === 200) {
            this.removeItem(id);
          }
        })
        .catch(() => {
          this.removeItem(id);
        });
    },
  },
};
</script>

<style scoped lang="less">
.rt-alarm-overlay {
  position: absolute;
  top: 50%;
  z-index: 888;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 1.25vw;
  background: transparent;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.25s ease, visibility 0.25s;
  pointer-events: none;

  &.open {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }

  &.open .rt-alarm-modal {
    transform: none;
    opacity: 1;
  }
}

.rt-alarm-modal {
  width: 33.33vw;
  max-width: 640px;
  max-height: 78vh;
  display: flex;
  flex-direction: column;
  background: #0b2348;
  border: 1px solid rgba(90, 160, 255, 0.45);
  border-radius: 6px;
  box-shadow: 0 18px 50px rgba(0, 8, 24, 0.45),
    0 0 30px rgba(30, 110, 230, 0.18);
  transform: translateY(12px);
  opacity: 0;
  transition: transform 0.3s ease, opacity 0.25s ease;
}

.rt-alarm-head {
  display: flex;
  align-items: center;
  gap: 0.52vw;
  padding: 0.73vw 0.83vw 0.68vw 1.04vw;
  flex: none;
  border-bottom: 1px solid rgba(90, 160, 255, 0.28);
  background: rgba(45, 125, 240, 0.16);

  h3 {
    margin: 0;
    font-size: 0.78vw;
    font-weight: 600;
    color: #f2f8ff;
    letter-spacing: 1px;
  }

  .num {
    font-size: 0.68vw;
    color: #8fb6e4;
    font-weight: 400;
  }

  .close {
    margin-left: auto;
    width: 26px;
    height: 26px;
    border: none;
    background: transparent;
    color: #8fb6e4;
    cursor: pointer;
    border-radius: 4px;
    display: grid;
    place-items: center;
    transition: 0.18s;

    &:hover {
      background: rgba(70, 140, 255, 0.25);
      color: #fff;
    }
  }
}

.rt-alarm-body {
  padding: 0 1.04vw;
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 9px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(90, 160, 255, 0.35);
    border-radius: 9px;
    border: 3px solid transparent;
    background-clip: padding-box;
  }
}

.rt-alarm-item {
  display: flex;
  align-items: center;
  gap: 0.83vw;
  padding: 0.78vw 0;
  border-bottom: 1px dashed rgba(90, 160, 255, 0.2);
  overflow: hidden;
  transition: height 0.3s ease, opacity 0.24s ease, padding 0.3s ease;

  &:last-child {
    border-bottom: none;
  }

  p {
    margin: 0;
    flex: 1;
    font-size: 0.7vw;
    line-height: 1.9;
    color: #d3e3f7;
    text-align: justify;

    /deep/ b {
      color: #ffffff;
      font-weight: 600;
    }
  }

  &.removing {
    opacity: 0;
    padding-top: 0;
    padding-bottom: 0;

    p {
      transform: translateX(14px);
      transition: transform 0.3s ease;
    }
  }
}

.ok {
  flex: none;
  border: 1px solid rgba(120, 185, 255, 0.5);
  background: rgba(45, 125, 240, 0.9);
  color: #fff;
  font-size: 0.68vw;
  font-family: inherit;
  padding: 0.36vw 0.94vw;
  border-radius: 4px;
  cursor: pointer;
  letter-spacing: 1px;
  transition: 0.18s;

  &:hover {
    background: rgba(70, 155, 255, 1);
    border-color: rgba(160, 210, 255, 0.8);
  }

  &:active {
    background: rgba(30, 105, 220, 1);
  }
}

.rt-alarm-empty {
  padding: 1.2vw 0;
  text-align: center;
  font-size: 0.73vw;
  color: #8fb6e4;
}

.rt-alarm-toast {
  position: absolute;
  left: 50%;
  top: 18%;
  transform: translate(-50%, 20px);
  z-index: 1100;
  padding: 10px 18px;
  border-radius: 4px;
  background: rgba(10, 34, 74, 0.94);
  border: 1px solid rgba(100, 170, 255, 0.55);
  color: #d8ecff;
  font-size: 0.68vw;
  letter-spacing: 1px;
  box-shadow: 0 0 30px rgba(40, 120, 255, 0.35);
  opacity: 0;
  visibility: hidden;
  transition: 0.3s;

  &.show {
    opacity: 1;
    visibility: visible;
    transform: translate(-50%, 0);
  }
}
</style>
